import { useEffect, useRef, useState } from 'react';
import { Banner, Button, Modal, Select, Spin, Tag, Typography } from '@douyinfe/semi-ui';
import { IconPlay, IconRefresh, IconSetting } from '@douyinfe/semi-icons';
import { AdminAPIError, api } from './api';
import { integrationError } from './appBossIntegration';
import { enrollmentPayload, enrollmentReason, enrollmentSelectionReadiness, enrollmentStatusLabel, initialEnrollmentSelection, refreshEnrollmentSelection, type AppBossEnrollmentPreview, type AppBossEnrollmentResult, type AppBossEnrollmentSelection } from './entranceEnrollment';
import './appBossEnrollment.css';
import { parseTranslationExecution, translationExecutionLimitsText } from './translationExecution';
import { checkAndEnableEntrance } from './enableAppBossEntrance';
import { lifecycleEnableBlockedReason } from './entranceLifecycle';
import { EntranceBillingLimitsForm } from './EntranceBillingLimitsForm';
import type { EntranceBillingLimits } from './entranceEnrollment';

const { Text } = Typography;
const emptySelection: AppBossEnrollmentSelection = { integrationId: '', models: [], operations: [] };

export function AppBossEnrollment({ sourceId, onClose, onChanged, onManage, onDenied }: {
  sourceId: string; onClose: () => void; onChanged: () => void; onManage: (entranceId: string) => void; onDenied: () => void;
}) {
  const [view, setView] = useState<AppBossEnrollmentPreview | null>(null);
  const [selection, setSelection] = useState<AppBossEnrollmentSelection>(emptySelection);
  const [result, setResult] = useState<AppBossEnrollmentResult | null>(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [denied, setDenied] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [refreshed, setRefreshed] = useState(false);
  const [activationNotice, setActivationNotice] = useState(''), [activationUncertain, setActivationUncertain] = useState(false);
  const previousSelection = useRef<AppBossEnrollmentSelection | null>(null);
  const createdChild = useRef<AppBossEnrollmentResult | null>(null);
  const mounted = useRef(false), pending = useRef(false), blocked = useRef(false), epoch = useRef(0), controller = useRef<AbortController>();
  const current = (generation: number) => mounted.current && !blocked.current && generation === epoch.current;
  function failure(value: unknown) {
    if (value instanceof AdminAPIError && [401, 403].includes(value.status)) {
      previousSelection.current = null; createdChild.current = null;
      blocked.current = true; setDenied(true); setView(null); setResult(null); setSelection(emptySelection); onDenied();
    }
    setError(value instanceof AdminAPIError && value.status === 404 && !value.code
      ? '当前服务器尚未提供“接入 AppBoss”功能，或原入口已不存在。请检查服务器版本和入口列表。'
      : integrationError(value));
  }
  async function load() {
    if (!mounted.current || blocked.current || pending.current) return;
    controller.current?.abort(); const read = new AbortController(); controller.current = read;
    setActivationNotice(''); setActivationUncertain(false);
    const generation = ++epoch.current; pending.current = true; createdChild.current = null; setBusy(true); setError(''); setView(null); setResult(null); setSelection(emptySelection); setRefreshed(false);
    try {
      const preview = await api.appBossEnrollment(sourceId, read.signal);
      if (!current(generation) || read.signal.aborted) return;
      const previous = previousSelection.current;
      const next = previous ? refreshEnrollmentSelection(preview, previous) : initialEnrollmentSelection(preview);
      previousSelection.current = next;
      setView(preview); setSelection(next); setRefreshed(previous !== null); setUncertain(false);
      if (uncertain && preview.existing.some((entry) => entry.integrationId === previous?.integrationId)) onChanged();
    } catch (value) { if (current(generation) && !read.signal.aborted) failure(value); }
    finally { if (generation === epoch.current) { pending.current = false; if (mounted.current) setBusy(false); } }
  }
  useEffect(() => {
    mounted.current = true; blocked.current = false; pending.current = false; previousSelection.current = null; setDenied(false); setUncertain(false); void load();
    return () => { mounted.current = false; ++epoch.current; controller.current?.abort(); };
  }, [sourceId]);
  const existing = view?.existing.find((entry) => entry.integrationId === selection.integrationId);
  const execution = parseTranslationExecution(view?.execution);
  const sharedSourceSupported = view?.sharedSourceSupported === true;
  const readinessReason = view && !existing ? enrollmentSelectionReadiness(view, selection) : undefined;
  let valid = false;
  if (view && !existing && (sharedSourceSupported || view.models.length <= 64)) { try { enrollmentPayload(view, selection); valid = true; } catch { /* Only the current server snapshot can authorize a selection. */ } }
  function choose(patch: Partial<AppBossEnrollmentSelection>) {
    if (pending.current || blocked.current || !mounted.current) return;
    const next = { ...(previousSelection.current || selection), ...patch };
    previousSelection.current = next; setSelection(next);
  }
  async function submit() {
    if (!view || !valid || result || createdChild.current || pending.current || blocked.current || !mounted.current) return;
    const generation = ++epoch.current; pending.current = true; setBusy(true); setError('');
    try {
      let saved: AppBossEnrollmentResult;
      try {
        saved = await api.enrollAppBossEntrance(view, selection);
      } catch (value) {
        if (current(generation)) { setView(null); setUncertain(true); failure(value); }
        return;
      }
      if (!current(generation)) return;
      createdChild.current = saved;
      setResult(saved); setUncertain(false); onChanged();
      if (saved.entrance.status === 'disabled') await activateChild(saved.entrance.id, generation);
    } finally { if (generation === epoch.current) { pending.current = false; if (mounted.current) setBusy(false); } }
  }
  async function enableChild(entranceId: string) {
    if (!mounted.current || blocked.current || pending.current) return;
    const owned = createdChild.current?.entrance.id === entranceId || result?.entrance.id === entranceId || view?.existing.some((entry) => entry.entranceId === entranceId);
    if (!owned || entranceId === sourceId) return;
    const generation = ++epoch.current; pending.current = true; setBusy(true);
    try {
      await activateChild(entranceId, generation);
    } finally {
      if (generation === epoch.current) {
        pending.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  }
  async function activateChild(entranceId: string, generation: number) {
    if (!current(generation) || entranceId === sourceId) return;
    const read = new AbortController(); controller.current = read;
    setError(''); setActivationNotice(''); setActivationUncertain(false);
    try {
      const enabled = await checkAndEnableEntrance(entranceId, true, { current: false }, () => current(generation), {
        read: api.entranceLifecycle,
        enable: (snapshot) => api.changeEntranceLifecycle(snapshot, 'enable', '', false, true)
      }, read.signal);
      if (!current(generation) || !enabled) return;
      if (!enabled.enabled) {
        setActivationNotice(enabled.view.state === 'deleted' ? '入口已删除，不能启用。' :
          lifecycleEnableBlockedReason({ ...enabled.view, canEnable: false }));
        return;
      }
      setResult((value) => value?.entrance.id === entranceId ? { ...value, entrance: { ...value.entrance, status: 'enabled' } } : value);
      setView((value) => value ? { ...value, existing: value.existing.map((entry) => entry.entranceId === entranceId ? { ...entry, status: 'enabled' } : entry) } : value);
      setActivationNotice('入口已启用。下一步：在 AppBoss 的应用列表中打开 AiGate，选择此入口并保存应用授权。');
      onChanged();
    } catch (value) {
      if (current(generation)) {
        setActivationUncertain(true);
        setActivationNotice('启用未完成或结果尚未确认。请重新检查并启用入口，不会重复创建。');
        failure(value);
      }
    }
  }
  const createdEntry = result?.entrance;
  async function configureSource(limits?:EntranceBillingLimits) {
    if(!view||pending.current||blocked.current||!mounted.current)return;
    if(!limits&&(!sharedSourceSupported||!view.canEnroll||!existing?.revision||existing.status==='deleted'))return;
    const generation=++epoch.current;pending.current=true;setBusy(true);setError('');
    let saved=false;
    try {
      if(limits)await api.configureEntranceBillingLimits(sourceId,view.source.revision,limits);
      else await api.shareAppBossSource(existing!.entranceId,existing!.revision!,view.source.revision);
      if(current(generation)){saved=true;onChanged();}
    }catch(value){if(current(generation))failure(value);}
    finally{if(generation===epoch.current){pending.current=false;if(mounted.current){setBusy(false);if(saved)void load();}}}
  }
  return <Modal title="接入 AppBoss" visible centered width="min(720px, calc(100vw - 24px))" className="appboss-enrollment-modal"
    onCancel={() => { if (!busy) onClose(); }} maskClosable={!busy} closeOnEsc={!busy} footer={null}>
    <div className="appboss-enrollment">
      {error && <Banner type="danger" closeIcon={null} description={error} />}
      {activationNotice && <Banner type={activationUncertain ? 'warning' : 'info'} closeIcon={null} description={activationNotice} />}
      {uncertain && !denied && <Text type="warning">尚未确认是否创建成功。重新加载会检查已有入口，不会自动重复创建。</Text>}
      {busy && <Spin aria-label="正在处理 AppBoss 接入" />}
      {!denied && !createdEntry && <div className="enrollment-top"><Text strong>{view?.source.name || '读取原入口'}</Text><Button aria-label="重新加载接入选项" icon={<IconRefresh />} disabled={busy} onClick={() => void load()} /></div>}
      {!denied && view && !createdEntry && <>
        <Text type="secondary">原入口、原地址及其 API Key 保持不变。</Text>
        {view.source.billingServiceId&&<details><summary>高级设置</summary><EntranceBillingLimitsForm key={view.source.revision} serviceId={view.source.billingServiceId} initial={view.source.billingLimits} busy={busy} onSave={(limits)=>void configureSource(limits)}/></details>}
        {refreshed && <Text type="secondary">选项已更新，仍可用的选择已保留，请重新核对。</Text>}
        {!view.canEnroll && <Banner type="warning" closeIcon={null} description={enrollmentReason(view.unavailableReason)} />}
        {view.canEnroll && !sharedSourceSupported && selection.routingMode !== 'shared_source' && view.models.length > 64 && !existing && <Text type="warning">当前模型目录超过旧版接入的 64 个模型上限，未自动选取部分模型。</Text>}
        {view.canEnroll && readinessReason && <Banner type="warning" closeIcon={null} description={enrollmentReason(readinessReason)} />}
        {view.canEnroll && readinessReason === 'qualification_unknown' && view.readinessReason && view.readinessReason !== 'adapter_unqualified' && <Banner type="warning" closeIcon={null} description={enrollmentReason(view.readinessReason)} />}
        {execution && <div className="enrollment-result">
          <Text strong>不通过 AppBoss 扣点数</Text>
          <details><summary>调用限制</summary><Text type="secondary">{translationExecutionLimitsText(execution, true)}</Text></details>
        </div>}
        {!view.integrations.length && <Button icon={<IconSetting />} onClick={() => { onClose(); window.location.assign('/appboss-integrations'); }}>配置 AppBoss 连接</Button>}
        {!!view.existing.length && !existing && <div className="enrollment-result"><Text strong>已接入的独立入口</Text>{view.existing.map((entry) => <div className="enrollment-existing" key={entry.entranceId}><Text>{entry.name}</Text><Tag color={entry.status === 'enabled' ? 'green' : 'grey'}>{enrollmentStatusLabel(entry.status)}</Tag><Button icon={<IconSetting />} disabled={busy} onClick={() => onManage(entry.entranceId)}>查看入口状态</Button></div>)}</div>}
        {!!view.integrations.length && <>
          <div className="enrollment-field"><Text id="enrollment-integration-label" strong>AppBoss 连接</Text><Select aria-labelledby="enrollment-integration-label" placeholder="选择连接" value={selection.integrationId || undefined} disabled={busy} onChange={(value) => choose({ integrationId: String(value) })} optionList={[...view.integrations.map((item) => ({ value: item.id, label: item.name })), ...(existing && !view.integrations.some((item) => item.id === selection.integrationId) ? [{ value: selection.integrationId, label: '原连接（当前不可用）', disabled: true }] : [])]} /></div>
        </>}
        {existing ? <div className="enrollment-result">
            <Text strong>{existing.status === 'deleted' ? '此连接的独立入口已删除，不能重复创建' : '已接入此 AppBoss，无需重复创建'}</Text><Text>{existing.name}</Text><code>{existing.entranceId}</code>
            <Tag color={existing.status === 'enabled' ? 'green' : 'grey'}>{enrollmentStatusLabel(existing.status)}</Tag>
            {existing.routingMode==='shared_source'?<Text>路由、模型与原收费服务沿用原入口。</Text>:existing.status!=='deleted'&&<><Text type="secondary">此入口保留原有独立配置。</Text>{sharedSourceSupported && view.canEnroll && <Button icon={<IconSetting/>} disabled={busy||!existing.revision} onClick={()=>Modal.confirm({title:'沿用原入口配置',content:'将改为读取原入口的路由、模型和原收费服务。旧令牌需要重新获取，原入口及 API Key 不变。',onOk:()=>configureSource()})}>沿用原入口配置</Button>}</>}
            {existing.status === 'disabled' && <Button theme="solid" icon={<IconPlay />} disabled={busy} onClick={() => void enableChild(existing.entranceId)}>检查并启用入口</Button>}
            <Button icon={<IconSetting />} disabled={busy} onClick={() => onManage(existing.entranceId)}>查看入口状态</Button>
          </div> : view.canEnroll && <Text>{selection.routingMode === 'shared_source' ? (sharedSourceSupported ? '沿用原入口的模型、路由与收费服务。' : '原入口的接入条件已变化，请重新核对。') : '使用原入口当前配置。'}</Text>}
      </>}
      {!denied && createdEntry && <div className="enrollment-result">
        <Text strong>{result.created ? '独立入口已创建' : '此连接已有独立入口，未重复创建'}</Text>
        <Text>{createdEntry.name}</Text><code>{createdEntry.id}</code>
        <Tag color={createdEntry.status === 'enabled' ? 'green' : 'grey'}>{createdEntry.status === 'enabled' ? '已启用' : '未启用'}</Tag>
        {(createdEntry.publicUrls || []).map((url) => <code key={url}>{url}</code>)}
        <Text>原入口及其 API Key 保持不变。</Text>
        <Text type="secondary">{selection.routingMode === 'shared_source' ? '沿用原入口的模型、路由与收费服务。' : '使用原入口当前配置。'}能否启用以此独立入口的服务器检查结果为准，还需在 AppBoss 对应应用中选择此入口并授权。</Text>
        {createdEntry.status === 'disabled' && <Button theme="solid" icon={<IconPlay />} disabled={busy} onClick={() => void enableChild(createdEntry.id)}>检查并启用入口</Button>}
        <Button icon={<IconSetting />} disabled={busy} onClick={() => onManage(createdEntry.id)}>查看入口状态</Button>
      </div>}
      <div className="enrollment-footer">
        <Button disabled={busy} onClick={onClose}>关闭</Button>
        {!denied && !createdEntry && view && !existing && <Button theme="solid" icon={<IconPlay />} loading={busy} disabled={busy || !valid} onClick={() => void submit()}>接入并启用</Button>}
      </div>
    </div>
  </Modal>;
}
